import * as XLSX from 'xlsx'

// Must match the backend's own cap (bulkCreateTeamMembers in
// yandle_admin_portal_1) -- checked here too so an oversized file is
// rejected immediately instead of only failing after a network round-trip.
export const MAX_BULK_TEAM_MEMBERS = 1000

const NAME_HEADERS = ['name', 'team member name', 'full name', 'member name']
const EMAIL_HEADERS = ['email', 'email address', 'e-mail']
const CONTACT_NUMBER_HEADERS = [
  'contact_number',
  'contact number',
  'phone',
  'phone_number',
  'mobile',
  'mobile_number',
  'number',
]

function normalizeHeader(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

function findColumnIndex(headers, candidates) {
  const normalized = headers.map(normalizeHeader)
  for (const candidate of candidates) {
    const needle = normalizeHeader(candidate)
    const idx = normalized.findIndex(
      (header) => header === needle || header.replace(/\s/g, '') === needle.replace(/\s/g, ''),
    )
    if (idx >= 0) return idx
  }
  return -1
}

function cellText(value) {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Avoid scientific notation for phone-like numbers from Excel.
    return String(value)
  }
  return String(value).trim()
}

/**
 * Parse a browser File (.xlsx / .xls / .csv) into
 * [{ name, email, contact_number }, ...].
 * Duplicate rows (same email) within the file are collapsed to the first
 * occurrence -- the backend also dedupes against what already exists in the
 * org (by email and by contact number), so this only handles within-file
 * repeats of the field the AI actually looks a person up by first.
 */
export async function parseBulkTeamMembersFile(file) {
  if (!file) {
    return { ok: false, error: 'Choose an Excel or CSV file.', members: [] }
  }

  try {
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    if (!sheetName) {
      return { ok: false, error: 'The spreadsheet has no sheets.', members: [] }
    }

    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      defval: '',
      raw: false,
    })

    if (!Array.isArray(rows) || rows.length < 2) {
      return {
        ok: false,
        error: 'Spreadsheet needs a header row and at least one data row.',
        members: [],
      }
    }

    const headers = (rows[0] || []).map((cell) => cellText(cell))
    const nameCol = findColumnIndex(headers, NAME_HEADERS)
    const emailCol = findColumnIndex(headers, EMAIL_HEADERS)
    const numberCol = findColumnIndex(headers, CONTACT_NUMBER_HEADERS)

    if (nameCol < 0 || emailCol < 0 || numberCol < 0) {
      return {
        ok: false,
        error: 'Columns required: name, email, and contact_number.',
        members: [],
      }
    }

    const members = []
    const seen = new Set()

    for (let i = 1; i < rows.length; i += 1) {
      const row = rows[i]
      if (!Array.isArray(row)) continue
      const name = cellText(row[nameCol])
      const email = cellText(row[emailCol]).toLowerCase()
      const contact_number = cellText(row[numberCol])
      if (!name && !email && !contact_number) continue
      if (!name || !email || !contact_number) continue

      if (seen.has(email)) continue
      seen.add(email)

      members.push({ name, email, contact_number })
    }

    if (members.length > MAX_BULK_TEAM_MEMBERS) {
      return {
        ok: false,
        error: `Maximum ${MAX_BULK_TEAM_MEMBERS} team members per upload -- this file has ${members.length}. Split it into smaller files.`,
        members: [],
      }
    }

    if (!members.length) {
      return {
        ok: false,
        error: 'No valid rows found (need name + email + contact_number).',
        members: [],
      }
    }

    return { ok: true, members, error: '' }
  } catch (error) {
    return {
      ok: false,
      error: error?.message || 'Failed to parse spreadsheet.',
      members: [],
    }
  }
}

/**
 * Builds and downloads a starter .xlsx with the exact headers this parser
 * looks for, plus one example row, so a user knows what to fill in without
 * guessing column names/order.
 */
export function downloadTeamMembersTemplate() {
  const worksheet = XLSX.utils.aoa_to_sheet([
    ['name', 'email', 'contact_number'],
    ['Jane Doe', 'jane@example.com', '9876543210'],
  ])
  worksheet['!cols'] = [{ wch: 20 }, { wch: 28 }, { wch: 18 }]

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Team members')
  XLSX.writeFile(workbook, 'team_members_bulk_upload_template.xlsx')
}
